import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { AvatarSourceDimensions } from '../../../lib/avatar-upload'
import 'react-image-crop/dist/ReactCrop.css'

interface AvatarCropDraft extends AvatarSourceDimensions {
  file: File
  previewUrl: string
  crop: Crop
  completedCrop: PixelCrop | null
}

interface AvatarDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userDisplayName: string
  resolvedAvatarPreview: string | null
  profileInitials: string
  avatarCropDraft: AvatarCropDraft | null
  onFileSelect: (file: File) => Promise<void>
  onReset: () => void
  onCropChange: (crop: Crop, completedCrop: PixelCrop | null) => void
  onSave: () => void
  isPending: boolean
}

function AvatarCropEditor({
  draft,
  onChange,
}: {
  draft: AvatarCropDraft
  onChange: (nextCrop: Crop, nextCompletedCrop: PixelCrop | null) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg bg-muted/30 p-4">
        <div className="flex min-h-[320px] items-center justify-center overflow-auto">
          <ReactCrop
            crop={draft.crop}
            aspect={1}
            keepSelection
            ruleOfThirds
            minWidth={120}
            onChange={(_pixelCrop, percentCrop) => {
              onChange(percentCrop, draft.completedCrop)
            }}
            onComplete={(pixelCrop, _percentCrop) => {
              onChange(draft.crop, pixelCrop)
            }}
            className="max-h-[70vh]"
          >
            <img
              src={draft.previewUrl}
              alt={t('settings.dialogs.avatar.currentPreview')}
              className="max-h-[70vh] w-auto max-w-full object-contain"
            />
          </ReactCrop>
        </div>
      </div>
    </div>
  )
}

export function AvatarDialog({
  open,
  onOpenChange,
  userDisplayName,
  resolvedAvatarPreview,
  profileInitials,
  avatarCropDraft,
  onFileSelect,
  onReset,
  onCropChange,
  onSave,
  isPending,
}: AvatarDialogProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const step = avatarCropDraft === null ? 'select' : 'crop'

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onReset()
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className={cn('sm:max-w-lg', step === 'crop' && 'sm:max-w-2xl')}>
        <DialogHeader>
          <DialogTitle>
            {step === 'select'
              ? t('settings.dialogs.avatar.title')
              : t('settings.dialogs.avatar.cropTitle')}
          </DialogTitle>
          <DialogDescription>
            {step === 'select'
              ? t('settings.dialogs.avatar.description')
              : t('settings.dialogs.avatar.cropDescription')}
          </DialogDescription>
        </DialogHeader>

        {step === 'select' ? (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-4 rounded-lg bg-muted/30 p-4">
              <Avatar className="size-20 border border-border bg-card">
                {resolvedAvatarPreview ? (
                  <AvatarImage src={resolvedAvatarPreview} alt={userDisplayName} />
                ) : null}
                <AvatarFallback className="text-lg font-semibold">{profileInitials}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {t('settings.dialogs.avatar.currentPreview')}
                </p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {t('settings.dialogs.avatar.acceptedFormats')}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-foreground">
                {t('settings.dialogs.avatar.selectImage')}
              </p>
              <input
                ref={fileInputRef}
                id="settings-avatar-file"
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/webp"
                onChange={async (event) => {
                  const nextFile = event.target.files?.[0] ?? null
                  if (nextFile === null) return
                  try {
                    await onFileSelect(nextFile)
                  } catch {
                    onReset()
                    event.currentTarget.value = ''
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="w-fit gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="size-4" />
                {resolvedAvatarPreview
                  ? t('settings.profile.changeAvatar')
                  : t('settings.dialogs.avatar.selectImage')}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t('settings.dialogs.avatar.acceptedFormats')}
              </p>
            </div>
          </div>
        ) : avatarCropDraft !== null ? (
          <AvatarCropEditor draft={avatarCropDraft} onChange={onCropChange} />
        ) : null}

        <DialogFooter>
          {step === 'crop' ? (
            <>
              <Button type="button" variant="outline" onClick={onReset}>
                {t('common.back')}
              </Button>
              <Button
                type="button"
                disabled={isPending || avatarCropDraft === null}
                onClick={onSave}
              >
                {isPending ? t('settings.dialogs.avatar.uploading') : t('settings.dialogs.avatar.saveAvatar')}
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
